import { api } from '../../services/api'

export const LEAVE_SESSION_STORAGE_KEY = 'clnicl_leave_session'

function rememberLeaveSession(token?: string) {
  if (typeof sessionStorage === 'undefined') return
  if (token) sessionStorage.setItem(LEAVE_SESSION_STORAGE_KEY, token)
  else sessionStorage.removeItem(LEAVE_SESSION_STORAGE_KEY)
}
import type {
  AdditionalHours,
  LeaveCalendar,
  LeaveEmail,
  LeaveEmployee,
  LeaveEmployeeInput,
  LeaveEmployeeWithBalance,
  LeaveRequest,
  LeaveSession,
  LeaveSettings,
  PayReport,
} from './types'

function byEmployeeName<T extends { name: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }))
}

export const leaveService = {
  getCalendar: async (month: string): Promise<LeaveCalendar> => {
    const response = await api.get<LeaveCalendar>('/leave/calendar', { params: { month } })
    return { ...response.data, employees: byEmployeeName(response.data.employees) }
  },
  getEmployees: async (): Promise<LeaveEmployeeWithBalance[]> => {
    const response = await api.get<LeaveEmployeeWithBalance[]>('/leave/employees')
    return byEmployeeName(response.data)
  },
  createEmployee: async (input: LeaveEmployeeInput): Promise<LeaveEmployee> => {
    const response = await api.post<LeaveEmployee>('/leave/employees', input)
    return response.data
  },
  updateEmployee: async (id: string, input: Partial<LeaveEmployeeInput>): Promise<LeaveEmployee> => {
    const response = await api.put<LeaveEmployee>(`/leave/employees/${id}`, input)
    return response.data
  },
  resetPin: async (id: string): Promise<{ pin: string }> => {
    const response = await api.post<{ pin: string }>(`/leave/employees/${id}/reset-pin`, {})
    return response.data
  },
  changePin: async (pin: string): Promise<void> => {
    await api.post('/leave/me/pin', { pin })
  },
  signIn: async (pin: string): Promise<LeaveSession> => {
    const response = await api.post<LeaveSession>('/leave/session', { pin })
    rememberLeaveSession(response.data.token)
    return response.data
  },
  signOut: async (): Promise<void> => {
    await api.delete('/leave/session')
    rememberLeaveSession()
  },
  getMe: async (): Promise<{ employee: LeaveEmployee; balance: LeaveSession['balance']; requests: LeaveRequest[] }> => {
    const response = await api.get<{ employee: LeaveEmployee; balance: LeaveSession['balance']; requests: LeaveRequest[] }>('/leave/me')
    return response.data
  },
  createRequest: async (input: {
    type: 'annual' | 'sick' | 'unpaid' | 'study'
    startDate: string
    endDate: string
    startPortion: 0.5 | 1
    endPortion: 0.5 | 1
    note?: string
  }): Promise<LeaveRequest> => {
    const response = await api.post<LeaveRequest>('/leave/requests', input)
    return response.data
  },
  createRequestForEmployee: async (input: {
    employeeId: string
    type: 'annual' | 'sick' | 'unpaid' | 'study'
    startDate: string
    endDate: string
    startPortion: 0.5 | 1
    endPortion: 0.5 | 1
    note?: string
  }): Promise<LeaveRequest> => {
    const response = await api.post<LeaveRequest>('/leave/requests/for-employee', input)
    return response.data
  },
  addAdditionalHours: async (input: {
    employeeId: string
    date: string
    hours: number
    note?: string
  }): Promise<AdditionalHours> => {
    const response = await api.post<AdditionalHours>('/leave/additional-hours', input)
    return response.data
  },
  removeAdditionalHours: async (id: string): Promise<void> => {
    await api.delete(`/leave/additional-hours/${id}`)
  },
  getRequests: async (): Promise<LeaveRequest[]> => {
    const response = await api.get<LeaveRequest[]>('/leave/requests')
    return response.data
  },
  approveRequest: async (id: string): Promise<LeaveRequest> => {
    const response = await api.post<LeaveRequest>(`/leave/requests/${id}/approve`, {})
    return response.data
  },
  declineRequest: async (id: string, decisionNote?: string): Promise<LeaveRequest> => {
    const response = await api.post<LeaveRequest>(`/leave/requests/${id}/decline`, { decisionNote })
    return response.data
  },
  cancelRequest: async (id: string): Promise<LeaveRequest> => {
    const response = await api.post<LeaveRequest>(`/leave/requests/${id}/cancel`, {})
    return response.data
  },
  sendBalanceEmail: async (): Promise<{ sentTo: string }> => {
    const response = await api.post<{ sentTo: string }>('/leave/balance-email', {})
    return response.data
  },
  getSettings: async (): Promise<LeaveSettings> => {
    const response = await api.get<LeaveSettings>('/leave/settings')
    return response.data
  },
  updateSettings: async (input: LeaveSettings): Promise<LeaveSettings> => {
    const response = await api.put<LeaveSettings>('/leave/settings', input)
    return response.data
  },
  sendAdminReport: async (): Promise<PayReport> => {
    const response = await api.post<PayReport>('/leave/pay-reports/current/notify', {})
    return response.data
  },
  getPayReport: async (): Promise<PayReport> => {
    const response = await api.get<PayReport>('/leave/pay-reports/current')
    return response.data
  },
  approvePayReport: async (): Promise<PayReport> => {
    const response = await api.post<PayReport>('/leave/pay-reports/current/approve', {})
    return response.data
  },
  getOutbox: async (): Promise<LeaveEmail[]> => {
    const response = await api.get<LeaveEmail[]>('/leave/outbox')
    return response.data
  },
}
